/* eslint-disable react-refresh/only-export-components */
import React, { useState } from 'react'
import { AquaLogo } from '../../assets/icons/AquaLogo'
import {
  FALLBACK_BRAND_NAME,
  useBranding,
  useBrandName,
  useLaunchName,
  usePlatformSuffixHidden,
} from '../../contexts/UiCustomizationContext'

/**
 * BrandLogo — renders the customer's brand mark: the configured company logo
 * image (when set) displayed alongside the gradient company-name title.
 *
 * The logo image is sourced from `branding.company_logo_url` and the title
 * text from `branding.company_name` (config/ui_customization/{key}.yaml). This
 * keeps the two independent — the image can be an icon-only mark while the
 * title is rendered as gradient text. The title gradient can be tuned to match
 * the image via `branding.logo_text_gradient`. When no logo URL is configured
 * — or the image fails to load — only the gradient title is shown.
 *
 * `size` is the logo height in px for the image and the font size for the
 * text, so both stay visually balanced.
 *
 * `suffix` (e.g. "Platform") is appended to the title and rendered in the same
 * gradient, so product sub-brands share the customer's logo colors.
 *
 * `launch` titles the mark with `branding.launch_name` instead of the company
 * name — for the pre-authentication screens, which carry the deployment's
 * launch brand (see useLaunchName).
 */
export const BrandLogo: React.FC<{
  size?: number
  className?: string
  suffix?: string
  launch?: boolean
}> = ({ size = 20, className = '', suffix, launch = false }) => {
  const branding = useBranding()
  const suffixHidden = usePlatformSuffixHidden()
  const { name: companyBrand, isResolved } = useBrandName()
  const { name: launchBrand } = useLaunchName()
  const [imgFailed, setImgFailed] = useState(false)

  const name = launch ? launchBrand : companyBrand
  // Deployments that suppress the suffix show the brand name on its own. Driven
  // by branding.hide_platform_suffix, which defaults to platform_only.
  const effectiveSuffix = suffixHidden ? undefined : suffix
  const text = effectiveSuffix ? `${name} ${effectiveSuffix}` : name
  const gradient = branding.logo_text_gradient || undefined
  const showImage = Boolean(branding.company_logo_url) && !imgFailed

  // Until branding resolves we know neither the company name nor whether the
  // "Platform" suffix applies. Reserve the fallback's footprint but keep it
  // invisible, so a white-labelled deployment never flashes the AQUA wordmark
  // on a slow connection (the layout stays stable instead of popping in).
  if (!isResolved) {
    return (
      <span
        className={className}
        style={{ display: 'inline-flex', alignItems: 'center', visibility: 'hidden' }}
        aria-hidden="true"
      >
        <AquaLogo size={size} text={FALLBACK_BRAND_NAME} gradient={gradient} />
      </span>
    )
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: showImage ? size * 0.4 : 0,
      }}
    >
      {showImage && (
        <img
          src={branding.company_logo_url as string}
          alt={`${name || FALLBACK_BRAND_NAME} logo`}
          style={{
            height: size,
            width: 'auto',
            maxWidth: '100%',
            display: 'inline-block',
            objectFit: 'contain',
          }}
          onError={() => setImgFailed(true)}
        />
      )}
      {/* Gradient title — always shown. When the logo image is present it sits
          beside an icon-only mark; otherwise it stands alone as the brand. */}
      <AquaLogo size={size} text={text} gradient={gradient} />
    </span>
  )
}

/**
 * Text equivalent of what <BrandLogo suffix={suffix}> paints, for the
 * `aria-label`/`title` of the control that wraps it — those used to hardcode
 * "AQUA Platform", which announces the vendor brand on a white-labelled
 * deployment. Mirrors BrandLogo's suffix rule so the label never says
 * "Platform" when the wordmark doesn't.
 */
export function useBrandLabel(suffix?: string): string {
  const suffixHidden = usePlatformSuffixHidden()
  const { name } = useBrandName()
  const effectiveSuffix = suffixHidden ? undefined : suffix
  return effectiveSuffix ? `${name} ${effectiveSuffix}` : name
}

export default BrandLogo
